if (!customElements.get('review-slideshow-component')) {
  customElements.define(
    'review-slideshow-component',
    class multicolumnSlider extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.swiper = this.querySelector('.swiper');
        if (!this.swiper) return; // No swiper container found

        this.sliderControls = this.querySelector('.swiper-controls');
        this.sliderPagination = this.querySelector('.swiper-pagination');
        this.enableSlider = this.swiper.dataset.enableSlider === 'true';

        if (!this.enableSlider) return; // Only init if enabled
        this.initSlider();
      }

      static get observedAttributes() {
        return ['selected-index'];
      }

      get selectedIndex() {
        return parseInt(this.getAttribute('selected-index')) || 0;
      }

      set selectedIndex(index) {
        this.setAttribute('selected-index', `${index}`);
      }

      initSlider() {
        const additionModules = [FoxTheme.Swiper.Autoplay, FoxTheme.Swiper.EffectFade];

        this.sliderOptions = {
          slidesPerView: 1,
          loop: true,
          effect: 'fade',
          fadeEffect: { crossFade: true },
          ...(this.sliderControls && {
            navigation: {
              nextEl: this.sliderControls.querySelector('.swiper-button-next'),
              prevEl: this.sliderControls.querySelector('.swiper-button-prev'),
            },
          }),
          ...(this.sliderPagination && {
            pagination: {
              el: this.sliderPagination,
              clickable: true,
            },
          }),
        };

        // Attach init callback safely
        if (typeof this.handleAfterInit === 'function') {
          this.sliderOptions.on = {
            init: this.handleAfterInit.bind(this),
          };
        }

        const autoplayDelay = parseInt(this.swiper.dataset.autoplay);
        if (autoplayDelay > 0) {
          this.sliderOptions.autoplay = {
            delay: autoplayDelay,
            disableOnInteraction: false,
          };
        }

        this.sliderInstance = new window.FoxTheme.Carousel(
          this.swiper,
          this.sliderOptions,
          additionModules
        );
        this.sliderInstance.init();

        if (this.sliderInstance?.slider) {
          this.sliderInstance.slider.on(
            'realIndexChange',
            this.handleSlideChange.bind(this)
          );

          // Store initial selected element safely
          this.selectedElement =
            this.sliderInstance.slider.slides[
              this.sliderInstance.slider.activeIndex
            ];

          if (this.selectedElement) {
            this.onReady(this.selectedElement, this.sliderInstance.slider.slides);
          }
        }

        // Shopify editor mode support
        if (Shopify.designMode) {
          document.addEventListener('shopify:block:select', (e) => {
            if (e.detail.sectionId !== this.swiper.dataset.sectionId) return;
            const target = e.target;
            const blockType = target.dataset.type;
            if (blockType !== 'slide') return;
            const index = Number(target.dataset.index);
            this.sliderInstance?.slider?.slideToLoop(index);
          });
        }

        if (this.sliderInstance) {
          const motionTextElements = Array.from(
            this.querySelectorAll('motion-element[data-text]')
          );
          motionTextElements.forEach((motionTextElement) => {
            if (
              typeof motionTextElement.resetAnimation === 'function' &&
              !motionTextElement.closest('.swiper-slide-active')
            ) {
              motionTextElement.resetAnimation();
            }
          });

          // Accessibility support
          const focusableElements = FoxTheme.a11y.getFocusableElements(this);
          focusableElements.forEach((element) => {
            if (!element.classList.contains('swiper-button')) {
              element.addEventListener('focusin', () => {
                const slide = element.closest('.swiper-slide');
                if (slide) {
                  this.sliderInstance?.slider?.slideToLoop(
                    this.sliderInstance.slider.slides.indexOf(slide)
                  );
                }
              });
            }
          });
        }
      }

      onReady(selectedElement) {
        if (!selectedElement) return; // ✅ prevent undefined crash

        if (!FoxTheme.config.motionReduced) {
          const motionEls = selectedElement.querySelectorAll('motion-element');
          motionEls.forEach((motionEl) => {
            if (motionEl && typeof motionEl.refreshAnimation === 'function') {
              motionEl.refreshAnimation();
            }
          });
        }
      }

      handleAfterInit() {
        // Fix active bullet transition issue
        if (this.sliderControls) {
          const activeBullet = this.sliderControls.querySelector(
            '.swiper-pagination-bullet-active'
          );
          if (activeBullet) {
            activeBullet.classList.remove('swiper-pagination-bullet-active');
            activeBullet.offsetHeight; // Trigger reflow
            activeBullet.classList.add('swiper-pagination-bullet-active');
          }
        }
      }

      handleSlideChange(swiper) {
        const { slides, realIndex, activeIndex } = swiper;
        this.selectedIndex = realIndex;
        this.updateControlsScheme(slides[activeIndex]);
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
          const fromElements = this.querySelectorAll(
            `[data-swiper-slide-index="${oldValue}"]`
          );
          const toElements = this.querySelectorAll(
            `[data-swiper-slide-index="${newValue}"]`
          );

          fromElements.forEach((fromElement) => {
            const motionEls = fromElement.querySelectorAll('motion-element');
            motionEls.forEach((motionEl) => {
              if (motionEl.hasAttribute('data-text')) {
                motionEl.resetAnimation();
              }
            });
          });

          toElements.forEach((toElement) => {
            setTimeout(() => {
              if (toElement.classList.contains('swiper-slide-active')) {
                const motionEls = toElement.querySelectorAll('motion-element');
                motionEls.forEach((motionEl) => {
                  if (motionEl && typeof motionEl.refreshAnimation === 'function') {
                    motionEl.refreshAnimation();
                  }
                });
              }
            });
          });
        }
      }

      updateControlsScheme(activeSlide) {
        if (this.sliderControls && activeSlide) {
          const classesToRemove = Array.from(this.sliderControls.classList).filter(
            (className) => className.startsWith('color-')
          );
          classesToRemove.forEach((className) =>
            this.sliderControls.classList.remove(className)
          );
          const colorScheme = activeSlide.dataset.colorScheme;
          if (colorScheme) {
            this.sliderControls.classList.add(colorScheme);
          }
        }
      }
    }
  );
}
